import { useEffect, useState } from 'react'
import './PwaInstallGuide.css'

// PWA 安装引导：iOS Safari 手动「添加到主屏幕」引导 + Android Chrome 安装横幅
// 已安装（standalone）/ 桌面 / 微信内 / Capacitor 壳内一律不渲染

type GuideEnv = 'installed' | 'ios-safari' | 'android-chrome' | 'other'

// Chrome/Edge 的 beforeinstallprompt 事件（TS 标准库无此类型）
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// 引导条关闭标记；key 带版本号，文案升级后可重新提示
const DISMISS_KEY = 'aie-pwa-guide-dismissed-v1'

function detectEnv(): GuideEnv {
  const ua = window.navigator.userAgent
  const standalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  if (standalone) return 'installed'
  if (/MicroMessenger/i.test(ua)) return 'other' // 微信内引导添加主屏体验差，不掺和
  // iOS Safari（排除 CriOS/FxiOS/EdgiOS 等三方浏览器，它们没有「添加到主屏幕」）
  if (/iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) return 'ios-safari'
  if (/Android/.test(ua) && /Chrome/.test(ua) && !/Chromium|EdgA|OPR|HeyTapBrowser/.test(ua)) {
    return 'android-chrome'
  }
  return 'other'
}

export default function PwaInstallGuide() {
  const [env, setEnv] = useState<GuideEnv>('other')
  const [dismissed, setDismissed] = useState(true)
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    setEnv(detectEnv())
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === '1')
    } catch {
      setDismissed(false)
    }
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setInstallEvent(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => setEnv('installed')
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (env !== 'ios-safari' && env !== 'android-chrome') return null
  if (dismissed) return null
  // Android 只有浏览器确认可安装（发出 beforeinstallprompt）才显示
  if (env === 'android-chrome' && !installEvent) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // 忽略
    }
    setDismissed(true)
  }

  const install = async () => {
    if (!installEvent) return
    await installEvent.prompt()
    await installEvent.userChoice
    setInstallEvent(null)
  }

  return (
    <div className="pwa-guide" role="banner">
      <div className="pwa-guide__text">
        {env === 'ios-safari' ? (
          <>把 AIE 装到主屏幕：点底部「分享」按钮，选「添加到主屏幕」，即可像 App 一样全屏使用</>
        ) : (
          <>把 AIE 装到手机桌面，像 App 一样全屏使用</>
        )}
      </div>
      {env === 'android-chrome' && (
        <button className="pwa-guide__btn" onClick={install}>
          安装
        </button>
      )}
      <button className="pwa-guide__close" onClick={dismiss} aria-label="关闭引导">
        ×
      </button>
    </div>
  )
}
